<template>
  <div class="dac-domain-row">
    <component
      :is="`icon-${isAvailable ? 'check' : 'transfer'}`"
      width="20"
      :class="[`dac-domain-icon`, `has-text-${color}`]"
    />
    <div class="dac-domain-name">
      <p class="dac-domain-fqdn">
        <span>{{ domain.sld }}</span
        ><strong>{{ domain.tld }}</strong>
      </p>
    </div>
    <p class="dac-domain-status" :class="[`has-text-${color}`]">
      {{ isAvailable ? "Available" : "Taken" }}
    </p>
    <p class="dac-domain-price">
      <!-- Discounted price -->
      <template v-if="domain.price_discounted_formatted">
        <span class="is-inline-flex has-items-aligned-center">
          <icon-promo width="14" class="has-text-danger has-margin-right-25" />
          <del class="is-size-7 has-text-danger has-margin-right-75">{{
            domain.price_formatted
          }}</del>
        </span>
        <span>{{ domain.price_discounted_formatted }}</span>
      </template>
      <!-- Non-Discounted Price -->
      <span v-else>{{ domain.price_formatted }}</span>
    </p>
    <p class="dac-domain-term">
      {{
        domain.billing_cycle_years === 1
          ? "/ Year"
          : `/ ${domain.billing_cycle_years} years`
      }}
      <template v-if="domain.price_discounted_formatted"
        >(Renews at {{ domain.price_formatted }})</template
      >
    </p>
    <template v-if="isAvailable">
      <a
        class="dac-domain-cta button is-success"
        :class="{ 'is-loading': isProcessing }"
        v-on="events"
        v-bind="props"
        @click="isProcessing = true"
      >
        <span class="is-hidden-mobile">Register domain</span>
        <icon-basket class="is-hidden-tablet" width="20" />
      </a>
    </template>
    <template v-else>
      <a
        class="dac-domain-cta button is-info"
        :class="{ 'is-loading': isProcessing }"
        v-on="events"
        v-bind="props"
        @click="isProcessing = true"
      >
        <span class="is-hidden-mobile">Transfer domain</span>
        <icon-transfer class="is-hidden-tablet" width="20" />
      </a>
    </template>
  </div>
</template>

<script>
export default {
  props: {
    domain: {
      type: Object,
      required: true,
    },
    onAddToBasket: {
      type: Function,
      required: false,
      default: null,
    },
  },
  components: {
    "icon-check": () => import("./IconCheck.vue"),
    "icon-promo": () => import("./IconPromo.vue"),
    "icon-transfer": () => import("./IconTransfer.vue"),
    "icon-basket": () => import("./IconBasket.vue"),
  },
  data() {
    return {
      isProcessing: false,
    };
  },
  computed: {
    events() {
      if (!this.onAddToBasket) return;
      return {
        ["click"]: ($event) => {
          $event.preventDefault();
          this.onAddToBasket(this.domain);
        },
      };
    },
    props() {
      if (this.onAddToBasket) return;
      return {
        href: this.domain.order_url,
      };
    },
    isAvailable() {
      return !!this.domain.domain_available;
    },
    color() {
      if (this.isAvailable) return "success";
      return "grey-light";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/sass/bulma/custom-variables.scss";

.dac-domain-row {
  display: grid;
  grid-column-gap: 1rem;
  padding: 0.5rem 0;
  grid-template-columns: auto 1fr auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  border: 1px solid transparent;
  transition: all ease-out 0.12s;
  &:hover {
    @include tablet {
      padding-left: 0.5rem;
    }
  }

  &:not(:first-child) {
    border-top-color: $border;
  }

  &.is-exact-match {
    background-color: $light;
    border-radius: $radius-large;
    padding: 1rem;
    margin: 0 -1rem;
    & + .dac-domain-row {
      margin-top: 1rem;
      border-top-color: transparent;
    }
  }

  .dac-domain-icon {
    grid-column: 1;
    grid-row: 1 / span 2;
  }

  .dac-domain-name {
    grid-column: 2;
    grid-row: 1;
    overflow: hidden;
    text-align: left;
    .dac-domain-fqdn {
      font-size: 1.125rem;
      overflow: hidden;
      text-overflow: ellipsis;
      direction: rtl;
      @include tablet {
        font-size: 1.25rem;
      }
      span {
        color: $grey-dark;
        font-weight: 300;
      }
    }
  }

  .dac-domain-status {
    grid-column: 2;
    grid-row: 2;
  }

  .dac-domain-price {
    grid-column: 3;
    grid-row: 1;
    font-size: 1rem;
    text-align: right;
    @include tablet {
      font-size: 1.125rem;
    }
  }

  .dac-domain-term {
    grid-column: 3;
    grid-row: 2;
    text-align: right;
    color: $grey;
  }

  .dac-domain-cta {
    grid-column: 4;
    grid-row: 1 / span 2;
  }

  .dac-domain-status,
  .dac-domain-term {
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
  }
}
</style>
